"use client";

import {
    listAdminOrganizationsApiV1AdminOrganizationsGet,
    listAdminCoursesApiV1AdminCoursesGet,
    createInteractiveLessonApiV1AdminInteractiveLessonsPost,
} from "@/src/api/sdk.gen";
import { useState, useRef, DragEvent, ChangeEvent, useEffect } from "react";
import { X, Upload, CheckCircle, AlertCircle, ArrowRight, ArrowLeft, FileText, Type, Plus, Building2 } from "lucide-react";

interface UploadLessonModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSuccess?: () => void;
    courseId?: number;
}

type UploadStatus = "idle" | "uploading" | "processing" | "completed" | "failed";

export function UploadLessonModal({ isOpen, onClose, onSuccess, courseId }: UploadLessonModalProps) {
    const [step, setStep] = useState<1 | 2 | 3>(1);
    const [title, setTitle] = useState("");
    const [domains, setDomains] = useState<string[]>([""]);
    const [futureGoals, setFutureGoals] = useState<string[]>([""]);
    const [courseFile, setCourseFile] = useState<File | null>(null);
    const [promptFile, setPromptFile] = useState<File | null>(null);
    const [proactivePromptFile, setProactivePromptFile] = useState<File | null>(null);
    const [quizPromptFile, setQuizPromptFile] = useState<File | null>(null);
    const [placementQuizPromptFile, setPlacementQuizPromptFile] = useState<File | null>(null);
    const [remedialPromptFile, setRemedialPromptFile] = useState<File | null>(null);
    const [status, setStatus] = useState<UploadStatus>("idle");
    const [selectedOrgId, setSelectedOrgId] = useState("");
    const [adminOrgs, setAdminOrgs] = useState<{ id: number; name: string }[]>([]);
    const [isOrgsLoaded, setIsOrgsLoaded] = useState(false);

    useEffect(() => {
        async function fetchOrgs() {
            try {
                const { data, error } = await listAdminOrganizationsApiV1AdminOrganizationsGet();
                if (!error && data) {
                    const orgs = data as unknown as { id: number; name: string }[];
                    setAdminOrgs(orgs);
                    if (orgs.length > 0 && !selectedOrgId) {
                        setSelectedOrgId(String(orgs[0].id));
                    }
                }
            } catch (err) {
                console.error("Failed to fetch admin organizations:", err);
            } finally {
                setIsOrgsLoaded(true);
            }
        }
        fetchOrgs();
    }, []);

    const [uploadId, setUploadId] = useState<number | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [isDragging, setIsDragging] = useState(false);
    const courseFileInputRef = useRef<HTMLInputElement>(null);
    const promptFileInputRef = useRef<HTMLInputElement>(null);
    const proactivePromptFileInputRef = useRef<HTMLInputElement>(null);
    const quizPromptFileInputRef = useRef<HTMLInputElement>(null);
    const placementQuizPromptFileInputRef = useRef<HTMLInputElement>(null);
    const remedialPromptFileInputRef = useRef<HTMLInputElement>(null);
    const pollIntervalRef = useRef<NodeJS.Timeout | null>(null);

    const resetState = () => {
        setStep(1);
        setTitle("");
        setDomains([""]);
        setFutureGoals([""]);
        setCourseFile(null);
        setPromptFile(null);
        setProactivePromptFile(null);
        setQuizPromptFile(null);
        setPlacementQuizPromptFile(null);
        setRemedialPromptFile(null);
        setStatus("idle");
        setUploadId(null);
        setError(null);
        setIsDragging(false);
        if (pollIntervalRef.current) {
            clearInterval(pollIntervalRef.current);
            pollIntervalRef.current = null;
        }
        isUploadingRef.current = false;
    };

    const handleClose = () => {
        resetState();
        onClose();
    };

    const validateCourseFile = (file: File): string | null => {
        if (!file.name.endsWith(".md")) {
            return "Only .md files are allowed for course content";
        }
        if (file.size > 5 * 1024 * 1024) {
            return "File size must be less than 5MB";
        }
        return null;
    };

    const validatePromptFile = (file: File): string | null => {
        if (!file.name.endsWith(".txt")) {
            return "Only .txt files are allowed for prompt content";
        }
        if (file.size > 1 * 1024 * 1024) {
            return "Prompt file size must be less than 1MB";
        }
        return null;
    };

    const handleFileSelect = (selectedFile: File) => {
        let validationError = null;

        if (step === 2) {
            validationError = validateCourseFile(selectedFile);
            if (!validationError) {
                setCourseFile(selectedFile);
                setError(null);
            }
        }

        if (validationError) {
            setError(validationError);
        }
    };

    const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        setIsDragging(true);
    };

    const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        setIsDragging(false);
    };

    const handleDrop = (e: DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        setIsDragging(false);

        const droppedFile = e.dataTransfer.files[0];
        if (droppedFile) {
            handleFileSelect(droppedFile);
        }
    };

    const pollUploadStatus = async (id: number) => {
        try {
            const { data, error } = await listAdminCoursesApiV1AdminCoursesGet();
            if (error || !data) {
                throw new Error("Failed to fetch upload status");
            }

            const resData = (data as any) || {};
            const item = ((resData.uploads || []) as any[]).find((u) => u.id === id);
            const statusStr = item?.status;

            if (statusStr?.toLowerCase() === "completed") {
                setStatus("completed");
                if (pollIntervalRef.current) {
                    clearInterval(pollIntervalRef.current);
                    pollIntervalRef.current = null;
                }
                if (onSuccess) {
                    onSuccess();
                }
            } else if (statusStr?.toLowerCase() === "failed") {
                setStatus("failed");
                setError(item?.error_message || "Indexing failed");
                if (pollIntervalRef.current) {
                    clearInterval(pollIntervalRef.current);
                    pollIntervalRef.current = null;
                }
            }
        } catch (err) {
            console.error("Error polling upload status:", err);
        }
    };

    const isUploadingRef = useRef(false);

    const handleUpload = async () => {
        if (!courseFile) return;
        if (isUploadingRef.current) return;

        if (!promptFile || !proactivePromptFile || !quizPromptFile || !placementQuizPromptFile || !remedialPromptFile) {
            setError("All 5 prompt files are required: Canonical Lesson, Proactive, Quiz, Placement Quiz, and Remedial Lesson.");
            return;
        }

        isUploadingRef.current = true;
        setStatus("uploading");
        setError(null);

        const formData = new FormData();
        formData.append("title", title);

        if (!courseId) {
            if (!selectedOrgId) {
                setError("Please select an organization");
                setStatus("failed");
                isUploadingRef.current = false;
                return;
            }
            formData.append("organization_id", selectedOrgId);
        }

        // Structure metadata as JSON
        const metadata = JSON.stringify({
            domains: domains.filter(d => d.trim()),
            futureGoals: futureGoals.filter(g => g.trim())
        });
        formData.append("metadata", metadata);
        if (courseId) {
            formData.append("course_id", courseId.toString());
        }
        formData.append("file", courseFile);
        if (promptFile) {
            formData.append("prompt_file", promptFile);
        }
        if (proactivePromptFile) {
            formData.append("proactive_prompt_file", proactivePromptFile);
        }
        if (quizPromptFile) {
            formData.append("quiz_prompt_file", quizPromptFile);
        }
        if (placementQuizPromptFile) {
            formData.append("placement_quiz_prompt_file", placementQuizPromptFile);
        }
        if (remedialPromptFile) {
            formData.append("remedial_lesson_prompt_file", remedialPromptFile);
        }

        try {
            const { data, error: reqErr } = await createInteractiveLessonApiV1AdminInteractiveLessonsPost({
                body: formData as any,
            });

            if (reqErr || !data) {
                const errObj = (reqErr || {}) as { error?: string };
                throw new Error(errObj.error || "Upload failed");
            }

            const resData = data as any;
            const uId = resData.uploadId || resData.id || 0;
            setUploadId(uId);
            setStatus("processing");

            // Start polling for status updates
            pollIntervalRef.current = setInterval(() => {
                pollUploadStatus(uId);
            }, 2000); // Poll every 2 seconds
        } catch (err) {
            isUploadingRef.current = false;
            setStatus("failed");
            setError(err instanceof Error ? err.message : "Upload failed");
        }
    };

    const handleNext = () => {
        if (step === 1) {
            if (!title.trim()) {
                setError("Please enter a course title");
                return;
            }
            if (!courseId && !selectedOrgId) {
                setError("Please select an organization");
                return;
            }
            if (!courseId) {
                const hasDomain = domains.some((d) => d.trim());
                const hasFutureGoal = futureGoals.some((g) => g.trim());
                if (!hasDomain) {
                    setError("Please add at least one domain");
                    return;
                }
                if (!hasFutureGoal) {
                    setError("Please add at least one future goal");
                    return;
                }
            }
            setStep(2);
            setError(null);
        } else if (step === 2) {
            if (!courseFile) {
                setError("Please upload a course file");
                return;
            }
            setStep(3);
            setError(null);
        }
    };

    const handleBack = () => {
        if (step === 2) setStep(1);
        else if (step === 3) setStep(2);
        setError(null);
    };

    const addDomain = () => setDomains([...domains, ""]);
    const removeDomain = (index: number) => setDomains(domains.filter((_, i) => i !== index));
    const updateDomain = (index: number, value: string) => {
        const newDomains = [...domains];
        newDomains[index] = value;
        setDomains(newDomains);
    };

    const addFutureGoal = () => setFutureGoals([...futureGoals, ""]);
    const removeFutureGoal = (index: number) => setFutureGoals(futureGoals.filter((_, i) => i !== index));
    const updateFutureGoal = (index: number, value: string) => {
        const newFutureGoals = [...futureGoals];
        newFutureGoals[index] = value;
        setFutureGoals(newFutureGoals);
    };

    if (!isOpen) return null;

    let headerTitle = "Upload New Course";
    let description = "Follow the steps to upload a new course lesson.";

    if (step === 1) {
        headerTitle = "Course Metadata";
        description = "Enter the course name and additional metadata.";
    } else if (step === 2) {
        headerTitle = "Course Content";
        description = "Select a markdown file containing the lesson content.";
    } else if (step === 3) {
        headerTitle = "Generation Prompts";
        description = "Select prompt files for lesson, quiz, placement quiz, and remedial lesson generation.";
    }

    if (status === "uploading") {
        headerTitle = "Uploading Course...";
        description = "Please wait while we upload your files.";
    }

    const courseAccept = ".md";

    const isNextDisabled =
        (step === 1 &&
            (!title.trim() ||
                (!courseId && !selectedOrgId) ||
                (!courseId && (!domains.some((d) => d.trim()) || !futureGoals.some((g) => g.trim()))))) ||
        (step === 2 && !courseFile);

    return (
        <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex min-h-screen items-center justify-center p-4">
                <div
                    className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm transition-opacity"
                    onClick={handleClose}
                />

                <div className="relative bg-white rounded-2xl shadow-2xl max-w-lg w-full p-6 ring-1 ring-slate-900/5 transform transition-all">
                    {/* Header */}
                    <div className="flex items-center justify-between mb-8">
                        <div>
                            {(status === "idle" || status === "uploading") && (
                                <>
                                    <h2 className="text-xl font-bold text-slate-900">{headerTitle}</h2>
                                    <p className="text-sm text-slate-500 mt-1">{description}</p>
                                </>
                            )}
                        </div>
                        <button
                            type="button"
                            onClick={handleClose}
                            className="p-2 -mr-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-full transition-all"
                        >
                            <X className="h-5 w-5" />
                        </button>
                    </div>

                    {/* Content */}
                    {status === "idle" || status === "uploading" ? (
                        <>
                            {/* Step Indicator */}
                            {(status === "idle") && (
                                <div className="flex items-center justify-center gap-2 mb-8">
                                    {[1, 2, 3].map((s) => (
                                        <div
                                            key={s}
                                            className={`h-2 w-full rounded-full transition-all ${step === s ? "bg-blue-600 shadow-sm" : step > s ? "bg-blue-200" : "bg-slate-100"
                                                }`}
                                        />
                                    ))}
                                </div>
                            )}

                            {step === 1 && (
                                <div className="space-y-6 max-h-[50vh] overflow-y-auto pr-2 custom-scrollbar">
                                    {!courseId && (
                                        <>
                                            <div>
                                                <label htmlFor="course-title" className="block text-sm font-medium text-slate-700 mb-2">
                                                    Course Name
                                                </label>
                                                <div className="relative">
                                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                                        <Type className="h-5 w-5 text-slate-400" />
                                                    </div>
                                                    <input
                                                        type="text"
                                                        id="course-title"
                                                        value={title}
                                                        onChange={(e) => {
                                                            setTitle(e.target.value);
                                                            if (error === "Please enter a course title") setError(null);
                                                        }}
                                                        placeholder="e.g. Introduction to Python"
                                                        className="block w-full pl-10 pr-3 py-2.5 border border-slate-300 rounded-xl leading-5 bg-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-sm transition-all shadow-sm"
                                                    />
                                                </div>
                                            </div>

                                            {/* Organization Select */}
                                            <div>
                                                <label className="block text-sm font-medium text-slate-700 mb-2">
                                                    Organization
                                                </label>
                                                <div className="relative">
                                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                                        <Building2 className="h-5 w-5 text-slate-400" />
                                                    </div>
                                                    <select
                                                        value={selectedOrgId}
                                                        onChange={(e) => {
                                                            setSelectedOrgId(e.target.value);
                                                            if (error === "Please select an organization") setError(null);
                                                        }}
                                                        required
                                                        disabled={!isOrgsLoaded || adminOrgs.length === 0}
                                                        className="block w-full pl-10 pr-3 py-2.5 border border-slate-300 rounded-xl leading-5 bg-white text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-sm transition-all shadow-sm appearance-none disabled:opacity-50"
                                                    >
                                                        {!isOrgsLoaded && <option value="">Loading organizations...</option>}
                                                        {isOrgsLoaded && adminOrgs.length === 0 && <option value="">No admin organizations found</option>}
                                                        {adminOrgs.map((org) => (
                                                            <option key={org.id} value={String(org.id)}>
                                                                {org.name}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </div>
                                            </div>

                                            {/* Domains */}
                                            <div>
                                                <div className="flex items-center justify-between mb-2">
                                                    <label className="block text-sm font-medium text-slate-700">
                                                        Target Domains {!courseId && <span className="text-red-600">(required)</span>}
                                                    </label>
                                                    <button
                                                        type="button"
                                                        onClick={addDomain}
                                                        className="text-xs font-bold text-blue-600 hover:text-blue-700 flex items-center gap-1 transition-colors"
                                                    >
                                                        <Plus className="h-3 w-3" /> Add Domain
                                                    </button>
                                                </div>
                                                <div className="space-y-2">
                                                    {domains.map((domain, index) => (
                                                        <div key={index} className="flex gap-2">
                                                            <input
                                                                type="text"
                                                                value={domain}
                                                                onChange={(e) => updateDomain(index, e.target.value)}
                                                                placeholder="e.g. Medical, Retail..."
                                                                className="block w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all shadow-sm"
                                                            />
                                                            {domains.length > 1 && (
                                                                <button
                                                                    type="button"
                                                                    onClick={() => removeDomain(index)}
                                                                    className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all"
                                                                >
                                                                    <X className="h-4 w-4" />
                                                                </button>
                                                            )}
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>

                                            {/* Future Goals */}
                                            <div>
                                                <div className="flex items-center justify-between mb-2">
                                                    <label className="block text-sm font-medium text-slate-700">
                                                        Target Future Goals {!courseId && <span className="text-red-600">(required)</span>}
                                                    </label>
                                                    <button
                                                        type="button"
                                                        onClick={addFutureGoal}
                                                        className="text-xs font-bold text-blue-600 hover:text-blue-700 flex items-center gap-1 transition-colors"
                                                    >
                                                        <Plus className="h-3 w-3" /> Add Goal
                                                    </button>
                                                </div>
                                                <div className="space-y-2">
                                                    {futureGoals.map((goal, index) => (
                                                        <div key={index} className="flex gap-2">
                                                            <input
                                                                type="text"
                                                                value={goal}
                                                                onChange={(e) => updateFutureGoal(index, e.target.value)}
                                                                placeholder="e.g. AI Manager, Data Scientist..."
                                                                className="block w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all shadow-sm"
                                                            />
                                                            {futureGoals.length > 1 && (
                                                                <button
                                                                    type="button"
                                                                    onClick={() => removeFutureGoal(index)}
                                                                    className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all"
                                                                >
                                                                    <X className="h-4 w-4" />
                                                                </button>
                                                            )}
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        </>
                                    )}
                                    {courseId && (
                                        <div>
                                            <label htmlFor="lesson-title" className="block text-sm font-medium text-slate-700 mb-2">
                                                Lesson Title
                                            </label>
                                            <div className="relative">
                                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                                    <Type className="h-5 w-5 text-slate-400" />
                                                </div>
                                                <input
                                                    type="text"
                                                    id="lesson-title"
                                                    value={title}
                                                    onChange={(e) => {
                                                        setTitle(e.target.value);
                                                        if (error === "Please enter a lesson title") setError(null);
                                                    }}
                                                    placeholder="e.g. Introduction to Variables"
                                                    className="block w-full pl-10 pr-3 py-2.5 border border-slate-300 rounded-xl leading-5 bg-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-sm transition-all shadow-sm"
                                                />
                                            </div>
                                            <p className="text-xs text-slate-500 mt-2">
                                                Domain and Future Goals will be automatically fetched from the course.
                                            </p>
                                        </div>
                                    )}
                                </div>
                            )}

                            {step === 2 && (
                                <div
                                    onDragOver={handleDragOver}
                                    onDragLeave={handleDragLeave}
                                    onDrop={handleDrop}
                                    onClick={() => courseFileInputRef.current?.click()}
                                    className={`
                                        relative overflow-hidden group
                                        border-2 border-dashed rounded-xl p-10 text-center cursor-pointer
                                        transition-all duration-300 ease-out
                                        ${isDragging
                                            ? "border-blue-500 bg-blue-50/50 scale-[1.02]"
                                            : "border-slate-300 hover:border-blue-400 hover:bg-slate-50 hover:shadow-inner"
                                        }
                                        ${courseFile ? "bg-slate-50 border-blue-500 border-solid" : ""}
                                    `}
                                >
                                    <div className="absolute inset-0 bg-grid-slate-100 [mask-image:linear-gradient(0deg,white,rgba(255,255,255,0.6))] pointer-events-none" />

                                    <div className="relative z-10 flex flex-col items-center">
                                        <div className={`
                                            p-4 rounded-full mb-4 transition-all duration-300
                                            ${isDragging || courseFile ? "bg-blue-100 text-blue-600" : "bg-slate-100 text-slate-400 group-hover:bg-blue-50 group-hover:text-blue-500"}
                                        `}>
                                            <Upload className="h-8 w-8" />
                                        </div>

                                        {courseFile ? (
                                            <div className="animate-in fade-in zoom-in duration-200">
                                                <p className="text-lg font-bold text-slate-900 mb-1 truncate max-w-[250px] mx-auto">
                                                    {courseFile.name}
                                                </p>
                                                <p className="text-sm font-medium text-slate-500">
                                                    {(courseFile.size / 1024).toFixed(1)} KB • Ready
                                                </p>
                                            </div>
                                        ) : (
                                            <>
                                                <p className="text-lg font-semibold text-slate-700 mb-2">
                                                    Click or Drag file to upload
                                                </p>
                                                <p className="text-sm text-slate-500 max-w-xs mx-auto">
                                                    Support for {courseAccept} files up to 5MB.
                                                </p>
                                            </>
                                        )}
                                    </div>

                                    <input
                                        ref={courseFileInputRef}
                                        type="file"
                                        accept={courseAccept}
                                        onChange={(e) => {
                                            const file = e.target.files?.[0];
                                            if (file) handleFileSelect(file);
                                        }}
                                        className="hidden"
                                    />
                                </div>
                            )}

                            {step === 3 && (
                                <div className="space-y-4 max-h-[40vh] overflow-y-auto px-1">
                                    {/* Canonical Prompt */}
                                    <div
                                        onClick={() => promptFileInputRef.current?.click()}
                                        className={`
                                            p-4 border border-dashed border-slate-300 rounded-xl cursor-pointer hover:bg-slate-50 transition-colors
                                            ${promptFile ? "bg-blue-50 border-blue-500 border-solid" : ""}
                                        `}
                                    >
                                        <div className="flex items-center gap-4">
                                            <div className="p-2 bg-slate-100 rounded-lg text-slate-500">
                                                <FileText className="h-6 w-6" />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <p className="text-sm font-bold text-slate-900">Canonical Lesson Prompt <span className="text-red-600">(required)</span></p>
                                                {promptFile ? (
                                                    <p className="text-xs text-slate-500 truncate">{promptFile.name}</p>
                                                ) : (
                                                    <p className="text-xs text-slate-400">Click to select .txt file</p>
                                                )}
                                            </div>
                                            {promptFile && <CheckCircle className="h-5 w-5 text-blue-600" />}
                                        </div>
                                        <input
                                            ref={promptFileInputRef}
                                            type="file"
                                            accept=".txt"
                                            onChange={(e) => {
                                                const file = e.target.files?.[0];
                                                if (file) {
                                                    const error = validatePromptFile(file);
                                                    if (!error) setPromptFile(file);
                                                    else setError(error);
                                                }
                                            }}
                                            className="hidden"
                                        />
                                    </div>

                                    {/* Proactive Prompt */}
                                    <div
                                        onClick={() => proactivePromptFileInputRef.current?.click()}
                                        className={`
                                            p-4 border border-dashed border-slate-300 rounded-xl cursor-pointer hover:bg-slate-50 transition-colors
                                            ${proactivePromptFile ? "bg-blue-50 border-blue-500 border-solid" : ""}
                                        `}
                                    >
                                        <div className="flex items-center gap-4">
                                            <div className="p-2 bg-slate-100 rounded-lg text-slate-500">
                                                <FileText className="h-6 w-6" />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <p className="text-sm font-bold text-slate-900">Proactive Lesson Prompt <span className="text-red-600">(required)</span></p>
                                                {proactivePromptFile ? (
                                                    <p className="text-xs text-slate-500 truncate">{proactivePromptFile.name}</p>
                                                ) : (
                                                    <p className="text-xs text-slate-400">Click to select .txt file</p>
                                                )}
                                            </div>
                                            {proactivePromptFile && <CheckCircle className="h-5 w-5 text-blue-600" />}
                                        </div>
                                        <input
                                            ref={proactivePromptFileInputRef}
                                            type="file"
                                            accept=".txt"
                                            onChange={(e) => {
                                                const file = e.target.files?.[0];
                                                if (file) {
                                                    const error = validatePromptFile(file);
                                                    if (!error) setProactivePromptFile(file);
                                                    else setError(error);
                                                }
                                            }}
                                            className="hidden"
                                        />
                                    </div>

                                    {/* Quiz Prompt */}
                                    <div
                                        onClick={() => quizPromptFileInputRef.current?.click()}
                                        className={`
                                            p-4 border border-dashed border-slate-300 rounded-xl cursor-pointer hover:bg-slate-50 transition-colors
                                            ${quizPromptFile ? "bg-blue-50 border-blue-500 border-solid" : ""}
                                        `}
                                    >
                                        <div className="flex items-center gap-4">
                                            <div className="p-2 bg-slate-100 rounded-lg text-slate-500">
                                                <FileText className="h-6 w-6" />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <p className="text-sm font-bold text-slate-900">Quiz Prompt <span className="text-red-600">(required)</span></p>
                                                {quizPromptFile ? (
                                                    <p className="text-xs text-slate-500 truncate">{quizPromptFile.name}</p>
                                                ) : (
                                                    <p className="text-xs text-slate-400">Click to select .txt file</p>
                                                )}
                                            </div>
                                            {quizPromptFile && <CheckCircle className="h-5 w-5 text-blue-600" />}
                                        </div>
                                        <input
                                            ref={quizPromptFileInputRef}
                                            type="file"
                                            accept=".txt"
                                            onChange={(e) => {
                                                const file = e.target.files?.[0];
                                                if (file) {
                                                    const error = validatePromptFile(file);
                                                    if (!error) setQuizPromptFile(file);
                                                    else setError(error);
                                                }
                                            }}
                                            className="hidden"
                                        />
                                    </div>

                                    {/* Placement Quiz Prompt */}
                                    <div
                                        onClick={() => placementQuizPromptFileInputRef.current?.click()}
                                        className={`
                                            p-4 border border-dashed border-slate-300 rounded-xl cursor-pointer hover:bg-slate-50 transition-colors
                                            ${placementQuizPromptFile ? "bg-blue-50 border-blue-500 border-solid" : ""}
                                        `}
                                    >
                                        <div className="flex items-center gap-4">
                                            <div className="p-2 bg-slate-100 rounded-lg text-slate-500">
                                                <FileText className="h-6 w-6" />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <p className="text-sm font-bold text-slate-900">Placement Quiz Prompt <span className="text-red-600">(required)</span></p>
                                                {placementQuizPromptFile ? (
                                                    <p className="text-xs text-slate-500 truncate">{placementQuizPromptFile.name}</p>
                                                ) : (
                                                    <p className="text-xs text-slate-400">Click to select .txt file</p>
                                                )}
                                            </div>
                                            {placementQuizPromptFile && <CheckCircle className="h-5 w-5 text-blue-600" />}
                                        </div>
                                        <input
                                            ref={placementQuizPromptFileInputRef}
                                            type="file"
                                            accept=".txt"
                                            onChange={(e) => {
                                                const file = e.target.files?.[0];
                                                if (file) {
                                                    const error = validatePromptFile(file);
                                                    if (!error) setPlacementQuizPromptFile(file);
                                                    else setError(error);
                                                }
                                            }}
                                            className="hidden"
                                        />
                                    </div>

                                    {/* Remedial Lesson Prompt */}
                                    <div
                                        onClick={() => remedialPromptFileInputRef.current?.click()}
                                        className={`
                                            p-4 border border-dashed border-slate-300 rounded-xl cursor-pointer hover:bg-slate-50 transition-colors
                                            ${remedialPromptFile ? "bg-blue-50 border-blue-500 border-solid" : ""}
                                        `}
                                    >
                                        <div className="flex items-center gap-4">
                                            <div className="p-2 bg-slate-100 rounded-lg text-slate-500">
                                                <FileText className="h-6 w-6" />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <p className="text-sm font-bold text-slate-900">Remedial Lesson Prompt <span className="text-red-600">(required)</span></p>
                                                {remedialPromptFile ? (
                                                    <p className="text-xs text-slate-500 truncate">{remedialPromptFile.name}</p>
                                                ) : (
                                                    <p className="text-xs text-slate-400">Click to select .txt file</p>
                                                )}
                                            </div>
                                            {remedialPromptFile && <CheckCircle className="h-5 w-5 text-blue-600" />}
                                        </div>
                                        <input
                                            ref={remedialPromptFileInputRef}
                                            type="file"
                                            accept=".txt"
                                            onChange={(e) => {
                                                const file = e.target.files?.[0];
                                                if (file) {
                                                    const error = validatePromptFile(file);
                                                    if (!error) setRemedialPromptFile(file);
                                                    else setError(error);
                                                }
                                            }}
                                            className="hidden"
                                        />
                                    </div>
                                </div>
                            )}

                            {/* Error Message */}
                            {error && (
                                <div className="mt-4 p-3 bg-red-50 border border-red-100 rounded-lg flex items-center gap-3 animate-in fade-in slide-in-from-top-1">
                                    <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0" />
                                    <p className="text-sm font-medium text-red-700">{error}</p>
                                </div>
                            )}

                            {/* Buttons */}
                            <div className="mt-8 flex gap-3">
                                {step < 3 ? (
                                    <>
                                        {step > 1 && (
                                            <button
                                                type="button"
                                                onClick={handleBack}
                                                className="px-4 py-2.5 bg-white border border-slate-300 text-slate-700 rounded-xl hover:bg-slate-50 hover:text-slate-900 font-bold transition-all shadow-sm flex items-center gap-2"
                                            >
                                                <ArrowLeft className="h-4 w-4" /> Back
                                            </button>
                                        )}
                                        <button
                                            type="button"
                                            onClick={handleClose}
                                            className="flex-1 px-4 py-2.5 bg-white border border-slate-300 text-slate-700 rounded-xl hover:bg-slate-50 hover:text-slate-900 font-bold transition-all shadow-sm"
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="button"
                                            onClick={handleNext}
                                            disabled={isNextDisabled}
                                            className={
                                                isNextDisabled
                                                    ? "flex-1 px-4 py-2.5 rounded-xl text-white font-bold transition-all shadow-md flex items-center justify-center gap-2 bg-slate-300 cursor-not-allowed text-slate-500 shadow-none"
                                                    : "flex-1 px-4 py-2.5 rounded-xl text-white font-bold transition-all shadow-md flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 hover:shadow-lg hover:-translate-y-0.5 active:translate-y-0"
                                            }
                                        >
                                            Next <ArrowRight className="h-4 w-4" />
                                        </button>
                                    </>
                                ) : (
                                    <>
                                        <button
                                            type="button"
                                            onClick={handleBack}
                                            disabled={status === "uploading"}
                                            className="px-4 py-2.5 bg-white border border-slate-300 text-slate-700 rounded-xl hover:bg-slate-50 hover:text-slate-900 font-bold transition-all shadow-sm flex items-center gap-2"
                                        >
                                            <ArrowLeft className="h-4 w-4" /> Back
                                        </button>
                                        <button
                                            type="button"
                                            onClick={handleUpload}
                                            disabled={
                                                status === "uploading" ||
                                                !promptFile ||
                                                !proactivePromptFile ||
                                                !quizPromptFile ||
                                                !placementQuizPromptFile ||
                                                !remedialPromptFile
                                            }
                                            className={`
                                                flex-1 px-4 py-2.5 rounded-xl text-white font-bold transition-all shadow-md
                                                ${status === "uploading" ||
                                                !promptFile ||
                                                !proactivePromptFile ||
                                                !quizPromptFile ||
                                                !placementQuizPromptFile ||
                                                !remedialPromptFile
                                                    ? "bg-slate-300 cursor-not-allowed text-slate-500 shadow-none"
                                                    : "bg-blue-600 hover:bg-blue-700 hover:shadow-lg hover:-translate-y-0.5 active:translate-y-0"
                                                }
                                            `}
                                        >
                                            {status === "uploading" ? (
                                                <span className="flex items-center justify-center gap-2">
                                                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                                    Uploading...
                                                </span>
                                            ) : "Upload Course"}
                                        </button>
                                    </>
                                )}
                            </div>
                        </>
                    ) : status === "processing" ? (
                        <div className="text-center py-12 animate-in fade-in zoom-in duration-300">
                            <div className="relative inline-flex mb-6">
                                <div className="w-20 h-20 border-4 border-slate-100 rounded-full" />
                                <div className="absolute inset-0 w-20 h-20 border-4 border-blue-600 rounded-full border-t-transparent animate-spin" />
                                <Upload className="absolute inset-0 m-auto h-8 w-8 text-blue-600 animate-pulse" />
                            </div>

                            <h3 className="text-xl font-bold text-slate-900 mb-2">
                                Generating Lesson...
                            </h3>
                            <p className="text-slate-500 text-sm max-w-xs mx-auto mb-8">
                                We are analyzing the lesson content and adding it to the database. This usually takes about 10-15 seconds.
                            </p>

                            <div className="max-w-xs mx-auto bg-slate-100 rounded-full h-1.5 overflow-hidden">
                                <div className="h-full bg-blue-600 animate-[loading_2s_ease-in-out_infinite] w-1/3 rounded-full" />
                            </div>
                        </div>
                    ) : status === "completed" ? (
                        <div className="text-center py-8 animate-in fade-in zoom-in duration-300">
                            <div className="inline-flex items-center justify-center w-20 h-20 bg-green-100 text-green-600 rounded-full mb-6 ring-8 ring-green-50">
                                <CheckCircle className="h-10 w-10" />
                            </div>
                            <h3 className="text-xl font-bold text-slate-900 mb-2">
                                Processing Complete
                            </h3>
                            <p className="text-slate-500 text-sm mb-8">
                                The lesson has been successfully generated and is now available in the system.
                            </p>
                            <div className="flex gap-3">
                                <button
                                    onClick={handleClose}
                                    className="flex-1 px-4 py-2.5 border border-slate-300 text-slate-700 rounded-xl hover:bg-slate-50 transition-colors font-bold"
                                >
                                    Done
                                </button>
                                <button
                                    onClick={resetState}
                                    className="flex-1 px-4 py-2.5 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors font-bold shadow-md"
                                >
                                    Upload Another
                                </button>
                            </div>
                        </div>
                    ) : (
                        <div className="text-center py-8 animate-in fade-in zoom-in duration-300">
                            <div className="inline-flex items-center justify-center w-20 h-20 bg-red-100 text-red-600 rounded-full mb-6 ring-8 ring-red-50">
                                <AlertCircle className="h-10 w-10" />
                            </div>
                            <h3 className="text-xl font-bold text-slate-900 mb-2">
                                Upload Failed
                            </h3>
                            <p className="text-slate-500 text-sm mb-6 max-w-xs mx-auto">
                                {error || "An unexpected error occurred. Please try again or contact support."}
                            </p>
                            <div className="flex gap-3">
                                <button
                                    onClick={handleClose}
                                    className="flex-1 px-4 py-2.5 border border-slate-300 text-slate-700 rounded-xl hover:bg-slate-50 transition-colors font-bold"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={resetState}
                                    className="flex-1 px-4 py-2.5 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors font-bold shadow-md"
                                >
                                    Try Again
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

